function truthyFlag(v) {
  return v === true || v === 1 || v === "1";
}

/** Customer-facing labels for per-pond role keys stored in the database. */
export const POND_ROLE_LABELS = {
  scientist: "Pond Manager",
  staff: "Pond Management Staff",
  angler: "Angler",
};

export function formatPondRoleKind(kind) {
  if (!kind) return "";
  const key = String(kind).toLowerCase();
  return POND_ROLE_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);
}

export function formatPondRolesDisplay(kinds) {
  if (!Array.isArray(kinds) || kinds.length === 0) return null;
  return kinds.map(formatPondRoleKind).join(" · ");
}

function isIndependentAccount(u) {
  if (!u || typeof u !== "object") return false;
  if (
    u.isIndependentScientist ||
    truthyFlag(u.IsIndependentScientist)
  ) {
    return true;
  }
  const noHomeFisher = u.FisherID == null || u.FisherID === "";
  const notOrgAdmin = !truthyFlag(u.IsAdmin) && !truthyFlag(u.IsFisherOwner);
  return !!(noHomeFisher && notOrgAdmin);
}

/** Short label for sidebar badge; full title for profile. */
export function describeUserRole(u, { short = false } = {}) {
  if (!u || typeof u !== "object") {
    return { title: "—", subtitle: null, badge: "—" };
  }
  if (u.isCompanyManager || u.IsCompanyManager) {
    return {
      title: "Pond Management Company",
      badge: short ? "Company" : "Company",
      subtitle: u.companyName || u.CompanyName
        ? `Manage pond owner accounts for ${u.companyName || u.CompanyName}.`
        : "Create and pay for pond owner accounts under your company.",
    };
  }
  if (isIndependentAccount(u)) {
    return {
      title: "Scientist",
      badge: "Scientist",
      subtitle: "Global account. Accept pond owner invitations to access assigned ponds.",
    };
  }
  if (truthyFlag(u.IsFisherOwner)) {
    return {
      title: "Pond Owner",
      badge: "Owner",
      subtitle: "Manage your team, ponds, and organization settings.",
    };
  }
  if (truthyFlag(u.IsAdmin) && !truthyFlag(u.IsFisherOwner)) {
    return {
      title: "Pond Manager",
      badge: "Pond Manager",
      subtitle: "Full access across this organization's ponds and settings.",
    };
  }
  const defaultRole = String(u.DefaultPondRole || u.defaultPondRole || "").toLowerCase();
  if (defaultRole === "scientist") {
    return {
      title: "Pond Manager",
      badge: "Pond Manager",
      subtitle: "View, input, print, and download data on assigned ponds.",
    };
  }
  if (defaultRole === "staff") {
    return {
      title: "Pond Management Staff",
      badge: "Pond Management Staff",
      subtitle: "Input and download data on assigned ponds.",
    };
  }
  if (defaultRole === "angler") {
    return {
      title: "Angler",
      badge: "Angler",
      subtitle: "Add catch and sampling data on assigned ponds (typically via the mobile app).",
    };
  }
  if (truthyFlag(u.isAnglerOnly)) {
    return {
      title: "Angler",
      badge: "Angler",
      subtitle: "Add catch and sampling data on assigned ponds (typically via the mobile app).",
    };
  }
  if (truthyFlag(u.hasAnyPondAssignment)) {
    if (truthyFlag(u.hasScientistPondAccess)) {
      return {
        title: "Pond Manager",
        badge: "Pond Manager",
        subtitle: "View, input, print, and download data on assigned ponds.",
      };
    }
    return {
      title: "Pond Management Staff",
      badge: "Pond Management Staff",
      subtitle: "Input and download data on assigned ponds.",
    };
  }
  if (u.hasAnyPondAssignment === false) {
    return {
      title: "Pond-assigned account",
      badge: "Pond team",
      subtitle: "No ponds assigned yet. Ask your owner to add you to ponds.",
    };
  }
  return {
    title: "Team member",
    badge: "Member",
    subtitle: "Sign in again to refresh access details, or ask your owner if something looks wrong.",
  };
}

export function getUserRoleBadgeLabel(u) {
  return describeUserRole(u).badge;
}
